import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { MpxService } from '@intigral/domain/lib/application/services/MpxService';
import { useQuery } from '@tanstack/react-query';
import { PROGRAM_TYPE } from '../config/constants';
import { getTvodPricingTemplatesApi } from '../services/serviceWrapper/tvodServiceWrapper';
import { transformResponseGetProductsByMediaId } from '../utils/transformResponse/mpxResponses';
import { useEffect, useState } from 'react';
import { getUserInfo } from '../utils/user';
import { isGuestUser } from '../config/guestMode';

interface ContentItem {
    mediaId: string;
    programType?: string;
    categories?: string[];
    productId?: string;
    assetId?: string;
}

interface PriceInfo {
    priceWithVat: number;
    currency: string;
}

interface TVODProduct {
    id: string;
    pricingTemplateId: string;
    mediaId: string;
}

interface TVODPurchased {
    productId: string;
    expiryDate?: string;
    productGUID?: string;
}

interface PriceListItem {
    isRented: boolean;
    productId: string;
    priceInfo: PriceInfo;
    expiryDate: string | null;
}

interface PriceList {
    [mediaId: string]: PriceListItem;
}

type PricingTemplates = Record<string, PriceInfo>;

const pricingTemplatesRef: PricingTemplates | null = null;

/**
 * Custom hook for managing TVOD price lists
 * @param content - Single content item or array of content items
 * @returns Object containing TVOD prices and status
 */
export const useTvod = (content: ContentItem | ContentItem[] | null) => {
    const isGuest = isGuestUser(getUserInfo());
    const tvod_list = getUserInfo()?.tvodList;
    const [tvodList, setTvodList] = useState(tvod_list);
    const contentItem = Array.isArray(content) ? content[0] : content;
    const isTvod = contentItem?.programType === PROGRAM_TYPE.MOVIE && contentItem?.categories?.includes('TVOD');
    const tvod: any = tvodList?.find(
        (tvod) => tvod?.productId === contentItem?.productId || tvod?.productGUID === contentItem?.assetId
    );
    const getMediaIds = () => {
        if (!content) return '';

        const res = Array.isArray(content)
            ? content.reduce<string>(
                  (acc, nextTvod) =>
                      acc === '' ? nextTvod.mediaId : nextTvod?.mediaId ? `${acc}|${nextTvod.mediaId}` : acc,
                  ''
              )
            : content.mediaId;
        return res;
    };

    const mediaIds = getMediaIds();

    const getPriceListByTemplateId = (products: TVODProduct[], pricingTemplates: PricingTemplates = {}): PriceList => {
        const response: PriceList = {};

        products?.forEach(({ id, pricingTemplateId, mediaId }) => {
            const priceInfo = pricingTemplates[pricingTemplateId];
            const isRented = tvodList?.some((purchasedTvod) => purchasedTvod.productId === id);
            const expiryDate = tvodList?.find((purchasedTvod) => purchasedTvod.productId === id)?.expiryDate ?? null;

            response[mediaId] = {
                isRented,
                productId: id,
                priceInfo,
                expiryDate,
            };
        });
        return response;
    };

    const { data: pricingTemplates } = useQuery({
        queryKey: ['tvod-pricing-templates'],
        queryFn: async () => {
            const result = await getTvodPricingTemplatesApi();
            return result as PricingTemplates;
        },

        staleTime: 5 * 60 * 1000,
        enabled: isTvod,
    });

    const { data: tvodProduts } = useQuery({
        queryKey: ['tvod-produts', mediaIds],
        queryFn: async () => {
            if (!mediaIds) return null;
            try {
                const response = await container
                    .resolve<MpxService>(DI.SERVICES.IMpxService)
                    .getProducts('all-product', { byScopeIds: mediaIds, range: '1-100' });
                return transformResponseGetProductsByMediaId(response) as TVODProduct[];
            } catch (err) {
                return null;
            }
        },
        enabled: !!mediaIds && !!pricingTemplates && isTvod,
        staleTime: 5 * 60 * 1000,
    });

    const { data: tvodPrices, refetch: refetchPrices } = useQuery({
        queryKey: [`tvod-prices`, mediaIds],
        queryFn: async () => {
            if (!mediaIds || tvodProduts?.length <= 0) return null;
            return getPriceListByTemplateId(tvodProduts, pricingTemplates);
        },
        enabled: tvodProduts?.length > 0 && !!pricingTemplates && isTvod,
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        if (!!mediaIds && !!pricingTemplates && isTvod && content && tvodProduts && refetchPrices) {
            refetchPrices();
        }
    }, [tvodList]);

    const tvodInfo = tvodPrices?.[mediaIds];
    if (!mediaIds) {
        const newPriceInfo = {
            priceWithVat: tvod?.price || '',
            currency: tvod?.currency || '',
        };
        return {
            priceInfo: isGuest ? { priceWithVat: '', currency: '' } : newPriceInfo,
            isRented: tvod?.expiryDate ? true : false,
            expiryDate: tvod?.expiryDate,
            isTvod,
            productId: tvod?.productId,
            setTvodList,
        };
    } else {
        return {
            ...tvodInfo,
            priceInfo: isGuest ? { priceWithVat: '', currency: '' } : tvodInfo?.priceInfo,
            isTvod,
            setTvodList,
        };
    }
};
